// @flow
import { createSelector } from 'reselect';
import { fromJS } from 'immutable';

import {
    getIsGroupForProduct,
    getIsHueProduct,
    allProductsIncludingOfflineByTypeSelector,
    getProductItemById,
    getProductItemsExcludingGroups,
    getStateForProduct,
    getTypeForProduct,
    getWhetherProductHasCapability as _getWhetherProductHasCapability
} from '~/core/state/products/selectors';
import { getConfigBound } from '~/core/state/config/selectors';
import { getGroupItems, getGroupById, getGroupConsumers } from '~/core/state/groups/selectors';
import { capabilities, deviceTypes, bulbTypes } from '~/core/constants/devices';
import { getIn } from '~/core/utils/immutable';
import { bindable } from '~/core/state/selectors';
import Maybe from 'folktale/maybe';

const getWhetherProductHasCapability = bindable(_getWhetherProductHasCapability);

const defaultSaturation = 99;

export const getBulbs = createSelector(getProductItemsExcludingGroups, products =>
    allProductsIncludingOfflineByTypeSelector(bulbTypes, products)
);

const getConsumers = getIn(['props', 'consumers']);

const bulbsAssignedToOtherGroups = (allGroups, currentGroup) => {
    return allGroups.chain(groups =>
        currentGroup
            .map(currentGroup =>
                groups
                    .filter(group => group.get('id') !== currentGroup.get('id'))
                    .map(group => getConsumers(group) || fromJS({}))
                    .flatten()
            )
            .orElse(() => Maybe.of(groups.map(getConsumers).flatten()))
    );
};

export const availableBulbsForGroup = createSelector(
    [getGroupById, getGroupConsumers, getGroupItems, getBulbs],
    (currentGroup, currentGroupProducts, allGroups, bulbs) => {
        const productsAssignedToOtherGroups = bulbsAssignedToOtherGroups(allGroups, currentGroup);

        return bulbs.filter(bulb => !productsAssignedToOtherGroups.getOrElse([]).includes(bulb.get('id'))).map(b => {
            const shouldSet = currentGroupProducts.map(consumers => consumers.includes(b.get('id')));
            return b.set('include', shouldSet.getOrElse(false));
        });
    }
);

export const bulbsInOtherGroups = createSelector(
    [getGroupById, getGroupItems, getBulbs],
    (currentGroup, allGroups, bulbs) => {
        const productsAssignedToOtherGroups = bulbsAssignedToOtherGroups(allGroups, currentGroup).getOrElse([]);

        return bulbs.filter(bulb => productsAssignedToOtherGroups.includes(bulb.get('id'))).map(b => {
            return b.set('include', false);
        });
    }
);

export const getIsToggling = createSelector(
    getProductItemById,
    productItem => !!(productItem && productItem.get('isToggling', false))
);

export const getIsSaving = createSelector(
    getProductItemById,
    productItem => !!(productItem && productItem.get('isSaving', false))
);

export const getHSVForBulb = createSelector(getStateForProduct, state => {
    const h = state.get('hue');
    const s = state.get('saturation');
    const v = state.get('value');

    return [h, s <= 0 ? defaultSaturation : s, v];
});

export const getTemperatureForBulb = createSelector(
    getStateForProduct,
    state => state && state.get('colourTemperature')
);

export const getCanChangeToWhite = createSelector(
    [
        getConfigBound('features:installPhilipsHue'),
        getWhetherProductHasCapability({
            capability: capabilities.LIGHT_WHITE
        }),
        getTypeForProduct
    ],
    (isReadyForHueLights, canChangeToWhite, bulbType) =>
        isReadyForHueLights ? canChangeToWhite : deviceTypes.COLOR_LIGHT === bulbType
);

export const getNameForBulb = createSelector(getStateForProduct, state => state && state.get('name'));

export const getColorModeForBulb = createSelector(getStateForProduct, state => state && state.get('colourMode'));

export const getBrightnessForBulb = createSelector(getProductItemById, getColorModeForBulb, (productItem, mode) => {
    return productItem && mode === 'COLOUR'
        ? productItem.getIn(['state', 'value'], 0)
        : productItem.getIn(['state', 'brightness'], 0);
});

export const getIsSchedulable = createSelector(
    [
        getWhetherProductHasCapability({
            capability: capabilities.SCHEDULE
        }),
        getIsGroupForProduct,
        getIsHueProduct,
        getConfigBound('features:installPhilipsHue'),
        getConfigBound('features:devicegroupSchedule'),
        getConfigBound('features:hueSchedule')
    ],
    (isCapable, isGroup, isHueProduct, bulbCapabilitesExist, groupScheduleEnabled, hueScheduleEnabled) => {
        if (isGroup) {
            return !!groupScheduleEnabled;
        }
        if (isHueProduct) {
            return !!hueScheduleEnabled;
        }
        return bulbCapabilitesExist ? isCapable : true;
    }
);



// WEBPACK FOOTER //
// ./src/core/state/products/bulbs/selectors.js